(() => {
  if (window.__macMultiSelectFiltersLoaded) return;
  window.__macMultiSelectFiltersLoaded = true;

  const PARAMS = {
    team: 'mac_team',
    player: 'mac_player',
    set: 'mac_set',
    'variation-tag': 'mac_vtag',
    'variation-title': 'mac_vtitle'
  };
  const ALL_PARAMS = [...Object.values(PARAMS), 'mac_page'];
  const PREVIEW_PARAMS = ['preview_theme_id', '_fd', 'pb'];
  const MORE_SPORT_TAGS = ['hockey', 'soccer', 'golf', 'tennis', 'nascar', 'racing', 'wrestling', 'wwe', 'ufc', 'mma', 'college', 'ncaa'];

  let activeRequest = null;

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const slugify = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const collectionHandle = () => {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const readValues = (param) => {
    const raw = new URL(window.location.href).searchParams.get(param);
    return raw ? raw.split(',').map((value) => value.trim()).filter(Boolean) : [];
  };

  const extractTagFromLink = (link, group) => {
    const label = link.textContent.trim();
    const fallback = group === 'variation-tag' ? `variation-${slugify(label)}` : slugify(label);

    try {
      const url = new URL(link.href, window.location.origin);
      const handle = collectionHandle();
      const decodedPath = decodeURIComponent(url.pathname);
      const marker = `/collections/${handle}/`;
      if (!handle || !decodedPath.includes(marker)) return fallback;

      const tagPath = decodedPath.split(marker)[1].split('/')[0];
      if (!tagPath) return fallback;
      const candidates = tagPath.split('+').filter(Boolean);
      if (candidates.length === 1) return candidates[0];

      const labelSlug = slugify(label);
      const exact = candidates.find((candidate) => candidate === labelSlug);
      if (exact) return exact;
      if (group === 'variation-tag') {
        const variationMatch = candidates.find((candidate) => candidate.endsWith(labelSlug));
        if (variationMatch) return variationMatch;
      }
      return labelSlug || candidates[candidates.length - 1];
    } catch (_) {
      return fallback;
    }
  };

  const createCheckbox = (text, value, group, checked, chip = false) => {
    const label = document.createElement('label');
    label.className = chip ? 'mac-filter-checkbox mac-filter-checkbox--chip' : 'mac-filter-checkbox';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.value = value;
    input.checked = checked;
    input.setAttribute('data-mac-filter-option', '');
    input.dataset.macFilterGroup = group;

    const textSpan = document.createElement('span');
    textSpan.className = 'mac-filter-checkbox__label';
    textSpan.textContent = text;

    label.append(input, textSpan);
    return label;
  };

  const enhanceList = (list, group) => {
    if (!list || list.dataset.macCheckboxReady === 'true') return;
    const selected = new Set(readValues(PARAMS[group]));

    list.querySelectorAll('li[data-filter-item]').forEach((item) => {
      const link = item.querySelector(':scope > a');
      if (!link) return;
      const text = link.textContent.trim();
      const value = extractTagFromLink(link, group);
      const checked = selected.has(value) || item.classList.contains('is-active');
      item.replaceChildren(createCheckbox(text, value, group, checked));
      item.classList.toggle('is-active', checked);
    });

    list.dataset.macCheckboxReady = 'true';
  };

  const enhanceQuickVariations = (container) => {
    if (!container || container.dataset.macCheckboxReady === 'true') return;
    const selected = new Set(readValues(PARAMS['variation-title']));
    const fragment = document.createDocumentFragment();

    container.querySelectorAll('a').forEach((link) => {
      const text = link.textContent.trim();
      fragment.append(createCheckbox(text, text, 'variation-title', selected.has(text), true));
    });

    container.replaceChildren(fragment);
    container.dataset.macCheckboxReady = 'true';
  };

  const collectSelections = (sidebar) => {
    const groups = {};
    Object.keys(PARAMS).forEach((group) => { groups[group] = []; });

    sidebar.querySelectorAll('[data-mac-filter-option]:checked').forEach((input) => {
      const group = input.dataset.macFilterGroup;
      if (groups[group]) groups[group].push(input.value);
    });
    return groups;
  };

  const quote = (value) => String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  const tagTerm = (value) => `tag:\"${quote(value)}\"`;
  const titleTerm = (value) => `title:\"${quote(value)}\"`;

  const orClause = (values, termBuilder) => {
    if (!values.length) return '';
    if (values.length === 1) return termBuilder(values[0]);
    return `(${values.map(termBuilder).join(' OR ')})`;
  };

  const buildSearchQuery = (groups) => {
    const clauses = [];
    const handle = collectionHandle();

    if (handle === 'more-sports') {
      clauses.push(`(${MORE_SPORT_TAGS.map(tagTerm).join(' OR ')})`);
    } else if (handle) {
      clauses.push(tagTerm(handle));
    }

    const team = orClause(groups.team, tagTerm);
    const player = orClause(groups.player, tagTerm);
    const set = orClause(groups.set, tagTerm);
    if (team) clauses.push(team);
    if (player) clauses.push(player);
    if (set) clauses.push(set);

    const variations = [
      ...groups['variation-tag'].map(tagTerm),
      ...groups['variation-title'].map(titleTerm)
    ];
    if (variations.length === 1) clauses.push(variations[0]);
    if (variations.length > 1) clauses.push(`(${variations.join(' OR ')})`);

    return clauses.join(' AND ');
  };

  const setStateUrl = (sidebar, resetPage = true) => {
    const groups = collectSelections(sidebar);
    const url = new URL(window.location.href);

    Object.entries(PARAMS).forEach(([group, param]) => {
      const values = groups[group];
      if (values.length) url.searchParams.set(param, values.join(','));
      else url.searchParams.delete(param);
    });

    if (resetPage) url.searchParams.delete('mac_page');
    window.history.replaceState({ macFilters: true }, '', url.toString());
    return groups;
  };

  const buildFetchUrl = (groups) => {
    const stateUrl = new URL(window.location.href);
    const hasFilters = Object.values(groups).some((values) => values.length > 0);

    if (!hasFilters) {
      const clean = new URL(stateUrl.toString());
      ALL_PARAMS.forEach((param) => clean.searchParams.delete(param));
      clean.searchParams.delete('page');
      return clean;
    }

    const searchUrl = new URL('/search', window.location.origin);
    searchUrl.searchParams.set('view', 'mac-filter');
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('q', buildSearchQuery(groups));

    const sortBy = stateUrl.searchParams.get('sort_by');
    if (sortBy) searchUrl.searchParams.set('sort_by', sortBy);

    const page = stateUrl.searchParams.get('mac_page');
    if (page && page !== '1') searchUrl.searchParams.set('page', page);

    PREVIEW_PARAMS.forEach((param) => {
      if (stateUrl.searchParams.has(param)) searchUrl.searchParams.set(param, stateUrl.searchParams.get(param));
    });

    return searchUrl;
  };

  const replaceResults = (doc, originalGridClass) => {
    const currentResults = document.getElementById('ResultsList');
    const incomingResults = doc.getElementById('ResultsList');
    if (!currentResults || !incomingResults) throw new Error('Product results could not be loaded.');

    const currentInner = currentResults.querySelector(':scope > div');
    const incomingInner = incomingResults.querySelector(':scope > div');
    if (!currentInner || !incomingInner) throw new Error('Product grid could not be loaded.');

    const incomingGrid = incomingInner.querySelector('.product-grid');
    if (incomingGrid && originalGridClass) incomingGrid.className = originalGridClass;

    currentInner.innerHTML = incomingInner.innerHTML;
  };

  const updateActiveStyles = (sidebar) => {
    sidebar.querySelectorAll('li[data-filter-item]').forEach((item) => {
      const checkbox = item.querySelector('[data-mac-filter-option]');
      if (checkbox) item.classList.toggle('is-active', checkbox.checked);
    });

    const clearButton = sidebar.querySelector('[data-mac-clear-filters]');
    if (clearButton) {
      clearButton.hidden = sidebar.querySelectorAll('[data-mac-filter-option]:checked').length === 0;
    }
  };

  const loadResults = async (sidebar, groups, originalGridClass) => {
    if (activeRequest) activeRequest.abort();
    activeRequest = new AbortController();

    const layout = sidebar.closest('.mac-sports-layout');
    const results = document.getElementById('ResultsList');
    layout?.classList.add('mac-filters-loading');
    results?.setAttribute('aria-busy', 'true');

    try {
      const url = buildFetchUrl(groups);
      const response = await fetch(url.toString(), {
        signal: activeRequest.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Filter request failed (${response.status}).`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      replaceResults(doc, originalGridClass);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[MAC filters]', error);
    } finally {
      if (!activeRequest?.signal.aborted) {
        layout?.classList.remove('mac-filters-loading');
        document.getElementById('ResultsList')?.removeAttribute('aria-busy');
      }
    }
  };

  const syncCheckboxesFromUrl = (sidebar) => {
    Object.entries(PARAMS).forEach(([group, param]) => {
      const selected = new Set(readValues(param));
      sidebar.querySelectorAll(`[data-mac-filter-option][data-mac-filter-group="${group}"]`).forEach((input) => {
        input.checked = selected.has(input.value);
      });
    });
    updateActiveStyles(sidebar);
  };

  const init = () => {
    const sidebar = document.querySelector('.mac-sports-sidebar');
    if (!sidebar || sidebar.dataset.macMultiselectReady === 'true') return;
    sidebar.dataset.macMultiselectReady = 'true';

    enhanceList(sidebar.querySelector('[id^="MacTeamList-"]'), 'team');
    enhanceList(sidebar.querySelector('[id^="MacPlayerList-"]'), 'player');
    enhanceList(sidebar.querySelector('[id^="MacSetList-"]'), 'set');

    const variationMenu = [...sidebar.querySelectorAll('.mac-sports-menu')].find((menu) =>
      menu.querySelector('summary')?.textContent.trim().startsWith('Variation')
    );
    enhanceList(variationMenu?.querySelector('.mac-filter-list'), 'variation-tag');
    enhanceQuickVariations(variationMenu?.querySelector('.mac-variation-quick'));

    const originalGridClass = document.querySelector('#ResultsList .product-grid')?.className || '';

    const top = sidebar.querySelector('.mac-sports-sidebar__top');
    if (top && !top.querySelector('[data-mac-clear-filters]')) {
      top.querySelector('a')?.remove();
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'mac-filter-clear-button';
      clear.textContent = 'Clear all';
      clear.setAttribute('data-mac-clear-filters', '');
      clear.hidden = true;
      top.append(clear);
    }

    syncCheckboxesFromUrl(sidebar);

    sidebar.addEventListener('change', (event) => {
      if (!event.target.matches('[data-mac-filter-option]')) return;
      const groups = setStateUrl(sidebar, true);
      updateActiveStyles(sidebar);
      loadResults(sidebar, groups, originalGridClass);
    });

    sidebar.querySelector('[data-mac-clear-filters]')?.addEventListener('click', () => {
      sidebar.querySelectorAll('[data-mac-filter-option]').forEach((input) => { input.checked = false; });
      const groups = setStateUrl(sidebar, true);
      updateActiveStyles(sidebar);
      loadResults(sidebar, groups, originalGridClass);
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('#ResultsList a[href]');
      if (!link) return;
      const url = new URL(link.href, window.location.origin);
      if (!url.pathname.includes('/search') || !url.searchParams.has('page')) return;

      event.preventDefault();
      const stateUrl = new URL(window.location.href);
      stateUrl.searchParams.set('mac_page', url.searchParams.get('page'));
      window.history.replaceState({ macFilters: true }, '', stateUrl.toString());
      const groups = collectSelections(sidebar);
      loadResults(sidebar, groups, originalGridClass);
      document.getElementById('ResultsList')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    window.addEventListener('popstate', () => {
      syncCheckboxesFromUrl(sidebar);
      const groups = collectSelections(sidebar);
      loadResults(sidebar, groups, originalGridClass);
    });

    const initialGroups = collectSelections(sidebar);
    if (Object.values(initialGroups).some((values) => values.length > 0)) {
      loadResults(sidebar, initialGroups, originalGridClass);
    }
  };

  ready(init);
})();
